import { NoConnectionMessage } from "#components/ui/NoConnectionMessage.tsx";
import { ConversationComposerMenu } from "#features/conversations/components/conversation-input/ConversationComposerMenu.tsx";
import { ConversationDictationButton } from "#features/conversations/components/conversation-input/ConversationDictationButton.tsx";
import type { ComposerEditorHandle, SlashMenuHandle } from "#features/conversations/components/conversation-input/composerEditor.ts";
import type { EditorListbox } from "#features/conversations/components/conversation-input/mentionSuggestion.ts";
import type { SlashContext } from "#features/conversations/components/conversation-input/slashContext.ts";
import { ConversationInputFiles } from "#features/conversations/components/conversation-input/ConversationInputFiles.tsx";
import { ConversationInputTargetedReply } from "#features/conversations/components/conversation-input/ConversationInputTargetedReply.tsx";
import { ConversationInputUpgradeBanner } from "#features/conversations/components/conversation-input/ConversationInputUpgradeBanner.tsx";
import { ConversationSubmitButton } from "#features/conversations/components/conversation-input/ConversationSubmitButton.tsx";
import { preloadConversationTiptapInput } from "#features/conversations/components/conversation-input/LazyConversationTiptapInput.ts";
import { TiptapComposerEditor } from "#features/conversations/components/conversation-input/TiptapComposerEditor.tsx";
import { ReasoningEffortMenu } from "#features/conversations/components/conversation-input/ReasoningEffortMenu.tsx";
import { useDictation } from "#features/conversations/hooks/useDictation.ts";
import { useFilePasteFromClipboard } from "#features/conversations/hooks/useFilePasteFromClipboard.ts";
import { findPendingApproval } from "#features/conversations/lib/toolApprovalRecords.ts";
import { useComposerDraftStore } from "#features/conversations/stores/useComposerDraftStore.ts";
import { useComposerEditorStore } from "#features/conversations/stores/useComposerEditorStore.ts";
import type { ConversationModel } from "#features/conversations/types/ConversationMessageModel.ts";
import type { FileModel } from "#features/conversations/types/FileModel.ts";
import { useGetQuotaUsageSuspenseQuery } from "#features/quotas/api/useGetQuotaUsageSuspenseQuery.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import { useAssistantOptionsStore } from "#stores/useAssistantOptionsStore.ts";
import type { Skill } from "#types/types.ts";
import { Button, Div, Flex, Form, Text, type FlexProps } from "@hopper-ui/components";
import { useFocusRing } from "@react-aria/focus";
import classNames from "classnames";
import { useHotkey } from "@tanstack/react-hotkeys";
import { useQueryClient } from "@tanstack/react-query";
import { type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { ErrorBoundary } from "react-error-boundary";
import "./ConversationInputForm.css";
import "./ConversationInputForm.dark.css";

interface Props extends Pick<FlexProps, "padding"> {
    // Set on a conversation page, so a reply that failed to send comes back into this composer.
    conversationId?: string;
    files: FileModel[];
    isMutating: boolean;
    autofocus: boolean;
    isAssistantGenerating?: boolean;
    stopButton?: ReactNode;
    showUpgradeBanner?: boolean;
    handleFileSelected: (selectedFiles: File[]) => void;
    removeFile: (file: FileModel) => void;
    onSubmit: (value: string) => void;
}

// Takes the editor's place when its chunk fails to load (a tab left open across a deploy) or it throws: only a reload
// fetches the current chunk, so the bar offers one rather than the route's error page.
const ComposerEditorUnavailable = () => {
    const { tShared } = useSharedTranslation();

    return (
        <Flex role="alert" alignItems="center" justifyContent="space-between" gap="inline-sm" width="100%">
            <Text size="sm" color="neutral-weak">{tShared("errors.composerUnavailable")}</Text>
            <Button type="button" variant="secondary" size="sm" onPress={() => window.location.reload()}>
                {tShared("reloadPage")}
            </Button>
        </Flex>
    );
};

export function ConversationInputForm({ conversationId, files, isMutating, isAssistantGenerating, autofocus, stopButton, showUpgradeBanner, padding, handleFileSelected, removeFile, onSubmit }: Props) {
    const [value, setValue] = useState("");
    const editorRef = useRef<ComposerEditorHandle | null>(null);
    const barRef = useRef<HTMLFormElement | null>(null);
    const [slashContext, setSlashContext] = useState<SlashContext | null>(null);
    const slashMenuRef = useRef<SlashMenuHandle | null>(null);
    const [slashListbox, setSlashListbox] = useState<EditorListbox | null>(null);
    // Before the quota query, which suspends: the editor's chunk then downloads while the page's data loads.
    preloadConversationTiptapInput();
    const { data: { quotaUsage: { limitReached } } } = useGetQuotaUsageSuspenseQuery();
    // On the always-mounted bar, where the editor's paste events bubble, so an editor that mounts later is covered too.
    useFilePasteFromClipboard(handleFileSelected, barRef);

    // Dictation is on by default; surfaces can opt out via the setAssistantOptions `showDictation: false` option.
    const showDictation = useAssistantOptionsStore(state => state.options.showDictation);
    const isDictationAvailable = showDictation !== false;
    const valueRef = useRef(value);
    valueRef.current = value;
    const getCurrentValue = useCallback(() => valueRef.current, []);
    const { status: dictationStatus, audioStream: dictationAudioStream, toggle: toggleDictation, stop: stopDictation } = useDictation({ enabled: isDictationAvailable, getCurrentValue, setValue });

    const dictationShortcutHint = useMemo(() =>
        navigator.userAgent.includes("Mac") ? "⌘D" : "Ctrl+D"
    , []);

    const trimmedValue = useMemo(() => value.trim(), [value]);

    const unsentReply = useComposerDraftStore(state =>
        (conversationId !== undefined ? state.unsentReplies[conversationId] : undefined));
    const clearUnsentReply = useComposerDraftStore(state => state.clearUnsentReply);
    useEffect(() => {
        if (unsentReply !== undefined && conversationId !== undefined) {
            setValue(current => (current ? `${unsentReply}\n${current}` : unsentReply));
            clearUnsentReply(conversationId);
        }
    }, [unsentReply, conversationId, clearUnsentReply]);

    useEffect(() => {
        if (autofocus) {
            setTimeout(() => editorRef.current?.focus(), 100);
        }
    }, [autofocus]);

    // The composer stays mounted through an approval, so when a decision closes the prompt, focus comes back here.
    const queryClient = useQueryClient();
    const hasPendingApproval = useSyncExternalStore(
        useCallback(onChange => queryClient.getQueryCache().subscribe(onChange), [queryClient]),
        () => conversationId !== undefined && findPendingApproval(queryClient.getQueryData<ConversationModel>(AiQueryKeys.GetConversation(conversationId))) !== null
    );
    const hadPendingApproval = useRef(hasPendingApproval);
    useEffect(() => {
        const isFocusLost = !document.activeElement || document.activeElement === document.body;
        if (hadPendingApproval.current && !hasPendingApproval && isFocusLost) {
            editorRef.current?.focus();
        }
        hadPendingApproval.current = hasPendingApproval;
    }, [hasPendingApproval]);

    // A press on one of the composer's own controls keeps its focus; anywhere else on the shell focuses the field.
    const handleFormClick = (event: MouseEvent<HTMLFormElement>) => {
        if (event.target instanceof Element && event.target.closest("button, [role='menu'], [role='dialog']")) {
            return;
        }
        editorRef.current?.focus();
    };

    const focusInput = useCallback((caret?: number) => {
        // Deferred past the tray's close, so its focus restore doesn't send focus back to the "+".
        setTimeout(() => editorRef.current?.focus(caret));
    }, []);

    const handleSkillSelect = useCallback(({ slug }: Skill) => {
        const caret = editorRef.current?.insertSkill(slug, slashContext);
        setSlashContext(null);
        focusInput(caret);
    }, [focusInput, slashContext]);

    const handleSlashClose = useCallback((clearFragment: boolean) => {
        if (clearFragment && slashContext) {
            focusInput(editorRef.current?.removeSlashFragment(slashContext));
        } else {
            focusInput();
        }
        setSlashContext(null);
    }, [focusInput, slashContext]);

    const handleOnSubmit = useCallback((event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        stopDictation();
        onSubmit(trimmedValue);
        setValue("");
    }, [onSubmit, stopDictation, trimmedValue]);

    // While the editor is unavailable nothing shows the text, so nothing may add to it or send it, files included (the
    // page's drop and the bar's paste read the store too), and its failure stops a running dictation, whose mic is then off.
    const isEditorUnavailable = useComposerEditorStore(state => state.isEditorUnavailable);
    const setEditorUnavailable = useComposerEditorStore(state => state.setEditorUnavailable);
    useEffect(() => () => setEditorUnavailable(false), [setEditorUnavailable]);

    const isSubmitDisabled = useMemo(() =>
        // `isMutating` blocks submit while a conversation is being created (home page: eager create on
        // file attach). Without it, a submit landing in that window would send the first message before
        // the attached file registers as "Uploading", dropping the attachment. On conversation pages
        // `isMutating` (message send) overlaps `isAssistantGenerating`, so it adds no new restriction.
        // A file that finished uploading but is still indexing (`isProcessing`) also blocks submit until
        // it is retrievable (see useUploadFiles).
        isAssistantGenerating || isEditorUnavailable || trimmedValue.length === 0 || isMutating
        || files.some(({ fileUploadStatus }) => fileUploadStatus === "Uploading")
        || files.some(({ isProcessing }) => isProcessing)
    , [isAssistantGenerating, isEditorUnavailable, trimmedValue, isMutating, files]);

    const isInputDisabled = useMemo(() => {
        return !!showUpgradeBanner && limitReached;
    }, [showUpgradeBanner, limitReached]);
    const isBarDisabled = isInputDisabled || isEditorUnavailable;
    const handleEditorError = useCallback(() => {
        stopDictation();
        setEditorUnavailable(true);
    }, [setEditorUnavailable, stopDictation]);

    const isDictating = dictationStatus === "listening" || dictationStatus === "starting";
    // The mic holds the send slot until there is text to send, and while it records, so a hot mic can always be stopped.
    // Mod+D stays live whenever the mic is hidden.
    const isMicInSendSlot = isDictationAvailable && (trimmedValue.length === 0 || isDictating);

    // Mod+D: same muscle memory as Google Meet's mic toggle; preventDefault suppresses the browser bookmark.
    // Stopping is always allowed (never trap a hot mic); starting is blocked while the assistant generates.
    useHotkey("Mod+D", () => {
        if (isBarDisabled || (isAssistantGenerating && !isDictating)) {
            return;
        }
        toggleDictation();
    }, { ignoreInputs: false, enabled: isDictationAvailable, requireReset: true });

    // A ring only when focus arrives from the keyboard, so the bar is quiet when the panel opens on a click.
    const { focusProps: focusRingProps, isFocusVisible } = useFocusRing({ within: true, isTextInput: true });

    const editorProps = {
        ref: editorRef,
        value,
        isDisabled: isInputDisabled,
        isSubmitDisabled,
        slashContext,
        slashMenuRef,
        setValue,
        onSlashContextChange: setSlashContext,
        onSubmit: handleOnSubmit
    };

    return (
        <Div>
            {isInputDisabled && <ConversationInputUpgradeBanner />}
            {/* Hopper's Form takes no focus handlers, so this wrapper tracks focus for the bar alone. */}
            <Div onFocus={focusRingProps.onFocus} onBlur={focusRingProps.onBlur}>
                <Form
                    ref={barRef}
                    className={classNames("wai-composer-glass wai-conversation-input-form", {
                        "wai-conversation-input-form--focus-visible": isFocusVisible,
                        "wai-conversation-input-form--over-banner": isInputDisabled
                    })}
                    width="100%"
                    position="relative"
                    gap="core_0"
                    onClick={handleFormClick}
                    onSubmit={handleOnSubmit}
                >
                    {files.length > 0 && <ConversationInputFiles files={files} removeFile={removeFile} />}
                    {!isMutating && <ConversationInputTargetedReply />}
                    {/* A page that passes its padding keeps its gutter; without one, the bar lines up with its trays: the text
                        where their rows start, the "+" and the send slot centred on their icon and status columns. */}
                    <Flex {...(padding === undefined ? { paddingX: "inset-lg", paddingTop: "inset-md", paddingBottom: "inset-xs" } as const : { padding })}>
                        {/* No Suspense of its own: while the Tiptap chunk loads the form suspends with the page, as it does for the
                            quota, so its effects (autofocus, the paste listener) run once the editor exists. If the chunk fails to
                            load (a tab left open across a deploy, a flaky network) or the editor throws, the bar asks for a reload. */}
                        <ErrorBoundary fallback={<ComposerEditorUnavailable />} onError={handleEditorError}>
                            <TiptapComposerEditor {...editorProps} trayAnchorRef={barRef} slashListbox={slashListbox} />
                        </ErrorBoundary>
                    </Flex>
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        gap="inline-sm"
                        {...(padding === undefined ? { paddingX: "inset-md", paddingBottom: "inset-sm" } as const : { padding, paddingTop: "core_0" } as const)}
                    >
                        <ConversationComposerMenu
                            trayAnchorRef={barRef}
                            isAttachDisabled={!!isAssistantGenerating || isBarDisabled}
                            isSkillSelectDisabled={isBarDisabled}
                            slashQuery={slashContext?.query ?? null}
                            slashMenuRef={slashMenuRef}
                            handleFileSelected={handleFileSelected}
                            onSkillSelect={handleSkillSelect}
                            onSlashClose={handleSlashClose}
                            focusInput={focusInput}
                            onSlashListboxChange={setSlashListbox}
                        />
                        <Flex alignItems="center" gap="inline-xs">
                            <ReasoningEffortMenu isDisabled={isInputDisabled} trayAnchorRef={barRef} />
                            <Div className="wai-composer-send-slot">
                                {isMutating && stopButton ? stopButton : isMicInSendSlot ? (
                                    <ConversationDictationButton
                                        status={dictationStatus}
                                        audioStream={dictationAudioStream}
                                        isDisabled={isBarDisabled || (!!isAssistantGenerating && !isDictating)}
                                        shortcutHint={dictationShortcutHint}
                                        onToggle={toggleDictation}
                                    />
                                ) : <ConversationSubmitButton isDisabled={isSubmitDisabled} />}
                            </Div>
                        </Flex>
                    </Flex>
                </Form>
            </Div>
            <NoConnectionMessage />
        </Div>
    );
}
